import { ReactElement, useCallback, useMemo, useEffect } from 'react';
import {
  Typography,
  Stack,
  TextField,
  MenuItem,
  DialogActions,
  DialogContentText,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useFormik, FormikHelpers } from 'formik';
import { isUndefined, reject } from 'lodash';
import { useSnackbar } from 'notistack';

import { useLazyRequest } from '~hooks/index';
import { updateStateItems } from '~services/item';
// eslint-disable-next-line max-len
import { ORDERS_GLOBALS_OPTIONS_WITHOUT_COLLECT_AND_WITHOU_VISITINS } from '~constants/ordersStatesOptions';
import { hasError, getHelperText } from '~utils/formHelpers';

import { DialogChangeOrderStateProps } from './types';
import {
  DialogChangeOrderStateContainer,
  DialogChangeOrderStateTitle,
  DialogChangeOrderStateTitleCloseIcon,
  DialogChangeOrderStateContent,
} from './styles';
import { FIELDS_NAME, initialValues, validationSchema } from './utils';

const DialogChangeOrderState = ({
  open,
  onClose,
  onChangeSuccess,
  excludeStateOption,
  itemsIds,
}: DialogChangeOrderStateProps): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const stateOptions = useMemo(() => {
    if (isUndefined(excludeStateOption))
      return ORDERS_GLOBALS_OPTIONS_WITHOUT_COLLECT_AND_WITHOU_VISITINS;

    return reject(ORDERS_GLOBALS_OPTIONS_WITHOUT_COLLECT_AND_WITHOU_VISITINS, {
      value: excludeStateOption,
    });
  }, [excludeStateOption]);

  const [, loading, , executeUpdateStateItems] = useLazyRequest({
    request: updateStateItems,
    withPostSuccess: () => {
      const message = `La modificación de estado fue realizada correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      onChangeSuccess?.();
    },
    withPostFailure: (err) => {
      let errorMessage =
        'No se pudo realizar la modificación, intenté nuevamente';

      if (err?.data?.data.message) {
        errorMessage = err.data.data.message;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      { setSubmitting }: FormikHelpers<typeof initialValues>,
    ) => {
      const itemStateTypeId = Number(values[FIELDS_NAME.NEW_STATE]);

      await executeUpdateStateItems({
        itemIds: itemsIds,
        itemStateTypeId,
      });

      setSubmitting(false);
    },
    [itemsIds, executeUpdateStateItems],
  );

  const {
    resetForm,
    submitForm,
    isSubmitting,
    getFieldProps,
    touched,
    errors,
    dirty,
    isValid,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });

  const disabledSubmit = useMemo(() => !dirty || !isValid, [dirty, isValid]);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DialogChangeOrderStateContainer open={open} maxWidth="md">
      <DialogChangeOrderStateTitle>
        <Typography component="h2" variant="h6">
          Modificar estado de pedidos
        </Typography>

        <DialogChangeOrderStateTitleCloseIcon
          aria-label="close"
          onClick={onClose}
          size="small"
        >
          <CloseIcon />
        </DialogChangeOrderStateTitleCloseIcon>
      </DialogChangeOrderStateTitle>

      <DialogChangeOrderStateContent dividers>
        <Stack spacing={2}>
          <DialogContentText color="text.secondary" variant="caption">
            Al confirmar esta acción, los pedidos seleccionados pasarán a
            registrar el nuevo estado. El cambio quedará registrado en el
            historial del pedido.
          </DialogContentText>

          <Alert
            severity="info"
            variant="standard"
            icon={false}
            sx={(currentTheme) => ({
              border: `1px solid ${currentTheme.palette.info.light}`,
              ...currentTheme.typography.caption,
            })}
          >
            Si un pedido se encuentra actualmente relacionado a una ruta en
            progreso, no se modificará su estado. Para modificar estados de
            pedidos de rutas en curso, cierre la parada mediante el envío de
            evidencia o desde la app del chofer.
          </Alert>

          <TextField
            fullWidth
            autoComplete="off"
            label="Nuevo estado"
            error={hasError(touched, errors, FIELDS_NAME.NEW_STATE)}
            helperText={getHelperText(touched, errors, FIELDS_NAME.NEW_STATE)}
            {...getFieldProps(FIELDS_NAME.NEW_STATE)}
            select
          >
            {stateOptions.map((option) => (
              <MenuItem key={option.value} value={String(option.value)}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogChangeOrderStateContent>

      <DialogActions>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={submitForm}
          loading={loading || isSubmitting}
          disabled={disabledSubmit}
        >
          Confirmar
        </LoadingButton>
      </DialogActions>
    </DialogChangeOrderStateContainer>
  );
};

export default DialogChangeOrderState;
